// Packages
import { useWindowWidth } from "@react-hook/window-size"
import feather from "feather-icons"
import * as React from "react"
import { twMerge } from "tw-merge"

// Utils
import { screenSizeMaxDefaultProps } from "@utils/utils"

// Props
type ElementClassesType = {
  content?: string
  lineClamp?: string
  readMoreButton?: string
  readMoreIcon?: string
}
interface Props {
  classes?: {
    active?: ElementClassesType
    default?: ElementClassesType
  }
  content: React.ReactElement | string
  readLessText?: string
  readMoreButton?: React.ReactElement
  readMoreText?: string
  readMoreIcon?: React.ReactElement
  useReadMoreText?: boolean
  title?: string
}
const defaultProps = {
  classes: {
    default: {
      lineClamp: "line-clamp-6",
    },
  },
  useReadMoreText: true,
}

// Default Component
export default function ReadMore(propsPassed: Props) {
  const props = {
    ...defaultProps,
    ...propsPassed,
    classes: {
      ...propsPassed.classes,
      default: {
        ...defaultProps.classes.default,
        ...propsPassed.classes?.default,
      },
    },
  }
  const [showAllText, setShowAllText] = React.useState(false)
  const [showReadMore, setShowReadMore] = React.useState(false)
  const contentRef = React.useRef(null)
  const windowWidth = useWindowWidth()

  React.useEffect(() => {
    const maxLinesByScreenSize = props.classes?.default?.lineClamp
      .split(" ")
      .map(lineClamp => Number(lineClamp.replace(/\D/g, "")))

    const {
      screenBreakpoints: { mobile, tablet },
    } = screenSizeMaxDefaultProps

    const maxLines =
      windowWidth >= tablet + 1
        ? maxLinesByScreenSize[2]
        : windowWidth >= mobile + 1
          ? maxLinesByScreenSize[1]
          : maxLinesByScreenSize[0]

    const style = window.getComputedStyle(contentRef?.current)
    const numLines =
      contentRef?.current?.offsetHeight / parseInt(style.lineHeight)

    if (numLines >= maxLines) {
      setShowReadMore(true)
    } else {
      if (showReadMore) setShowReadMore(false)
    }
  }, [windowWidth])

  const readMoreButtonAttributes = {
    onClick: () => setShowAllText(prevShowAllText => !prevShowAllText),
  }
  return (
    <div>
      {/* Used to get height of copy without margin/padding between elements*/}
      <div
        dangerouslySetInnerHTML={{ __html: props.content }}
        ref={contentRef}
        className={`absolute -z-[1] [&_*]:mb-0 [&_*]:p-0 ${
          props.classes?.default?.content || ""
        } ${
          showReadMore && !showAllText
            ? `${props.classes?.default?.lineClamp} ${
                props.classes?.active?.content || ""
              }`
            : ""
        }`}
      />
      <div
        dangerouslySetInnerHTML={{ __html: props.content }}
        className={`${props.classes?.default?.content || ""} ${
          showReadMore && !showAllText
            ? `${props.classes?.default?.lineClamp} ${
                props.classes?.active?.content || ""
              }`
            : ""
        }`}
      />
      {showReadMore &&
        (props.readMoreButton ? (
          React.cloneElement(props.readMoreButton, {
            ...readMoreButtonAttributes,
          })
        ) : (
          <button
            className={twMerge(
              `flex items-center gap-[5px] disclaimer--regular [&_*]:text-inherit ${
                props.classes?.default?.readMoreButton || ""
              }`,
            )}
            {...readMoreButtonAttributes}
          >
            {/* Read More Text */}
            {showAllText && props.useReadMoreText ? (
              <span>{props.readLessText || "Read Less"}</span>
            ) : (
              !showAllText &&
              props.useReadMoreText && (
                <span>{props.readMoreText || "Read More"}</span>
              )
            )}
            {props.readMoreIcon ? (
              React.cloneElement(props.readMoreIcon, {
                showAllText,
              })
            ) : (
              <div
                dangerouslySetInnerHTML={{
                  __html: feather.icons[
                    showAllText ? "chevron-up" : "chevron-down"
                  ].toSvg({
                    class: `${props.classes?.default?.readMoreIcon}`,
                  }),
                }}
              />
            )}
          </button>
        ))}
    </div>
  )
}
